import { Model, type ModelProps } from '@/infra/model'
import { once } from 'lodash-es'
import { computed, observable } from 'mobx'
import { Id } from '@/infra/id'
import { BillingProfile } from '@/features/billing/profile/BillingProfile'

export type BillingMethodProps = ModelProps<BillingMethod>

/**
 * The kind of billing method, with the details shown for it.
 */
export type BillingMethodType = StripeCardMethod | CreditMethod | UnknownMethod

/**
 * A card added through a Stripe token.
 */
export interface StripeMethod {
  readonly type: 'Stripe'
  readonly expMonth: number
  readonly expYear: number
  readonly brand: string
  readonly last4: string
}

/**
 * A card added through a Stripe setup intent.
 */
export interface StripeCardPaymentMethod {
  readonly type: 'StripeCardPaymentMethod'
  readonly expMonth: number
  readonly expYear: number
  readonly brand: string
  readonly last4: string
}

/**
 * A card, however it was added.
 */
export type StripeCardMethod = StripeMethod | StripeCardPaymentMethod

/**
 * Credit extended by the workspace.
 */
export interface CreditMethod {
  readonly type: 'Credit'
  readonly balance: number
  readonly limit: number
}

/**
 * A billing method this build doesn't know.
 */
export interface UnknownMethod {
  readonly type: 'Unknown'
  /**
   * The type the API sent.
   */
  readonly rawType: string
}

/**
 * A way to pay that belongs to a billing profile.
 */
export class BillingMethod extends Model<BillingMethod> {
  id = Id.none

  @observable accessor type: BillingMethodType = {
    type: 'Unknown',
    rawType: 'Unknown',
  }
  @observable accessor workspaceId = Id.none

  constructor(public readonly billingProfile: BillingProfile) {
    super()
  }

  /**
   * Default instance.
   */
  static get none() {
    return lazyNone()
  }

  @computed
  get isDefault() {
    return this.billingProfile.defaultBillingMethodId === this.id
  }

  /**
   * The card details, when this is a card.
   */
  @computed
  get stripeCard(): StripeCardMethod | null {
    return this.type.type === 'Stripe' ||
      this.type.type === 'StripeCardPaymentMethod'
      ? this.type
      : null
  }
}

const lazyNone = once(() => new BillingMethod(BillingProfile.none))
